import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { GoogleAuthProvider, signInWithPopup, signOut, updateEmail as firebaseUpdateEmail, onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth, hasFirebaseConfig } from '../firebase';

export const AuthContext = createContext();

const mapFirebaseAuthError = (error) => {
  switch (error?.code) {
    case 'auth/configuration-not-found':
      return 'Google Sign-In is not enabled in Firebase. In Firebase Console, go to Authentication > Sign-in method and enable Google.';
    case 'auth/operation-not-allowed':
      return 'Google Sign-In is currently disabled for this Firebase project.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for Firebase Auth. Add localhost to Authentication > Settings > Authorized domains.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed before completing authentication.';
    default:
      return error?.message || 'Google sign-in failed';
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  // Rehydrate session from Firebase Auth directly.
  useEffect(() => {
    if (!hasFirebaseConfig || !firebaseAuth) {
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setToken(null);
        return;
      }

      try {
        const idToken = await firebaseUser.getIdToken();
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
        });
        setToken(idToken);
      } catch {
        setUser(null);
        setToken(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      if (!hasFirebaseConfig || !firebaseAuth) {
        throw new Error('Google Sign-In is not configured. Set REACT_APP_FIREBASE_* values in frontend/.env and restart the frontend server.');
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const credential = await signInWithPopup(firebaseAuth, provider);
      const idToken = await credential.user.getIdToken();
      const mappedUser = {
        id: credential.user.uid,
        email: credential.user.email || '',
      };
      setUser(mappedUser);
      setToken(idToken);

      return { user: mappedUser, token: idToken };
    } catch (error) {
      throw new Error(mapFirebaseAuthError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(firebaseAuth);
    } catch {
      // Ignore sign-out sync issues and still clear local session.
    }
    setUser(null);
    setToken(null);
  }, []);

  const updateUserEmail = useCallback(async (newEmail) => {
    setLoading(true);
    try {
      if (!hasFirebaseConfig || !firebaseAuth || !firebaseAuth.currentUser) {
        throw new Error('Google Sign-In is not configured. Cannot update email.');
      }

      try {
        await firebaseUpdateEmail(firebaseAuth.currentUser, newEmail);
      } catch (error) {
        if (error.code === 'auth/requires-recent-login') {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await signInWithPopup(firebaseAuth, provider);
          await firebaseUpdateEmail(firebaseAuth.currentUser, newEmail);
        } else if (error.code === 'auth/email-already-in-use') {
          throw new Error('This email is already in use.');
        } else if (error.code === 'auth/invalid-email') {
          throw new Error('Please enter a valid email address.');
        } else {
          throw new Error(mapFirebaseAuthError(error));
        }
      }

      const freshToken = await firebaseAuth.currentUser.getIdToken(true);
      const mappedUser = {
        id: firebaseAuth.currentUser.uid,
        email: firebaseAuth.currentUser.email || newEmail,
      };
      setUser(mappedUser);
      setToken(freshToken);
      return { user: mappedUser, token: freshToken };
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    signInWithGoogle,
    logout,
    updateUserEmail,
  }), [user, token, loading, signInWithGoogle, logout, updateUserEmail]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
